import { cs } from '../../lib/currency';
import { useEffect, useMemo, useState } from 'react';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Package, Loader2, Plus, Minus } from 'lucide-react';
import api from '../../services/api';

/**
 * EquipmentPicker — add-on items a customer/admin can rent alongside a lane
 * booking. Given a date + time window, fetches live availability (remaining
 * units) and lets the user pick a quantity per item. Emits resolved lines
 * (`[{equipment_id, quantity}]`) via `onChange`, and a running total via
 * `onTotalChange` for the parent form to display.
 *
 * Props:
 *   date              — YYYY-MM-DD (if missing, component still lists items
 *                       but disables qty inputs until a date/time is set)
 *   startTime         — HH:MM
 *   endTime           — HH:MM
 *   excludeBookingId  — omit this booking from the reservation sum (useful
 *                       when editing an existing booking)
 *   value             — [{equipment_id, quantity}]
 *   onChange          — fn(nextValue)
 *   onTotalChange     — fn(subtotalDollars)
 *   compact           — render in a tighter row layout
 */
export default function EquipmentPicker({
  date,
  startTime,
  endTime,
  excludeBookingId,
  value = [],
  onChange,
  onTotalChange,
  compact = false,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const canFetch = Boolean(date && startTime && endTime);

  // Duration in hours (fractional allowed) for hourly-rate calculation.
  const hours = useMemo(() => {
    if (!startTime || !endTime) return 1;
    const toMin = (hhmm) => {
      const [h, m] = String(hhmm).split(':').map(Number);
      return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : 0;
    };
    const diff = toMin(endTime) - toMin(startTime);
    return diff > 0 ? diff / 60 : 1;
  }, [startTime, endTime]);

  useEffect(() => {
    let cancelled = false;
    if (!canFetch) {
      setItems([]);
      return () => { cancelled = true; };
    }
    setLoading(true);
    api
      .get('/lanes/equipment/availability', {
        params: {
          date,
          start_time: startTime,
          end_time: endTime,
          exclude_booking_id: excludeBookingId || undefined,
        },
      })
      .then((res) => {
        if (!cancelled) setItems(res.data?.items || []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date, startTime, endTime, excludeBookingId, canFetch]);

  const valueMap = useMemo(() => {
    const m = {};
    (value || []).forEach((l) => { m[l.equipment_id] = l.quantity; });
    return m;
  }, [value]);

  const total = useMemo(() => {
    let sum = 0;
    items.forEach((it) => {
      const qty = valueMap[it.equipment_id] || 0;
      const rate = Number(it.daily_rate || 0);
      const pricingType = (it.pricing_type || 'flat').toLowerCase();
      const units = pricingType === 'hourly' ? qty * hours : qty;
      sum += units * rate;
    });
    return Math.round(sum * 100) / 100;
  }, [items, valueMap, hours]);

  useEffect(() => {
    if (onTotalChange) onTotalChange(total);
  }, [total, onTotalChange]);

  const setQty = (equipment_id, nextQty, max) => {
    const clamped = Math.max(0, Math.min(max, Math.floor(Number(nextQty) || 0)));
    const rest = (value || []).filter((l) => l.equipment_id !== equipment_id);
    const next = clamped > 0 ? [...rest, { equipment_id, quantity: clamped }] : rest;
    onChange && onChange(next);
  };

  if (!canFetch) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 p-3 text-xs text-slate-500">
        <span className="flex items-center gap-1.5">
          <Package className="h-3.5 w-3.5" /> Equipment rentals
        </span>
        <p className="mt-1">Pick a date, start time, and duration to see what's available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm flex items-center gap-1.5">
          <Package className="h-4 w-4 text-blue-500" /> Equipment (optional)
        </Label>
        {total > 0 && (
          <span className="text-xs font-medium text-emerald-700">
            + {cs()}{total.toFixed(2)}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-3 text-slate-500 text-xs">
          <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Checking availability…
        </div>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-500 py-2">No equipment available right now.</p>
      ) : (
        <div className={`space-y-2 ${compact ? '' : 'max-h-48 overflow-y-auto'}`}>
          {items.map((it) => {
            const qty = valueMap[it.equipment_id] || 0;
            const available = Math.max(0, it.available);
            const maxQty = available + qty; // include already-selected qty
            return (
              <div
                key={it.equipment_id}
                className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2 bg-slate-50"
                data-testid={`equipment-picker-row-${it.equipment_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-800 truncate">{it.name}</span>
                    <span className="text-[11px] text-slate-500">
                      {cs()}{Number(it.daily_rate || 0).toFixed(2)}
                      /{(it.pricing_type || 'flat') === 'hourly' ? 'hr' : 'booking'}
                      {' · '}{available}/{it.total_quantity} available
                    </span>
                  </div>
                  {it.description && !compact && (
                    <p className="text-[11px] text-slate-500 truncate">{it.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setQty(it.equipment_id, qty - 1, maxQty)}
                    disabled={qty <= 0}
                    type="button"
                    data-testid={`equipment-dec-${it.equipment_id}`}
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </Button>
                  <Input
                    className="h-7 w-12 text-center p-0"
                    value={qty}
                    onChange={(e) => setQty(it.equipment_id, e.target.value, maxQty)}
                    data-testid={`equipment-qty-${it.equipment_id}`}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setQty(it.equipment_id, qty + 1, maxQty)}
                    disabled={qty >= maxQty}
                    type="button"
                    data-testid={`equipment-inc-${it.equipment_id}`}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
